import { Box, Skeleton } from "@mui/material";
import { useEffect } from "react";

import {
  PREVIEW_HEIGHT,
  PREVIEW_WIDTH,
  CLIP_PATHS,
  EMAIL_RENDER_WIDTH,
  EMAIL_SCALE_FACTOR,
} from "../constants";
import { useEmailPreviewSource } from "../useEmailPreviewSource";
import type { FramePreviewManifest } from "@/types/dragon-tiles";

import { useScrollingElement } from "@/hooks/useScrollingElement";
import {
  useScrollingFrame,
  wrapHtmlForScrollingFrame,
} from "@/hooks/useScrollingFrame";

export interface EmailCardPreviewProps {
  frameName: string;
  initialPreview?: string;
  manifest?: FramePreviewManifest | null;
  siteId?: string;
  variantId?: string;
}

export function EmailCardPreview({
  frameName,
  initialPreview,
  manifest,
  siteId,
  variantId,
}: EmailCardPreviewProps) {
  const {
    iframeRef,
    handleMouseEnter: handleIframeMouseEnter,
    handleMouseLeave: handleIframeMouseLeave,
    handleIframeLoad,
  } = useScrollingFrame();

  const {
    elementRef: imageScrollRef,
    handleMouseEnter: handleImageMouseEnter,
    handleMouseLeave: handleImageMouseLeave,
  } = useScrollingElement<HTMLDivElement>({ pixelsPerFrame: 1 });

  const {
    imageRef,
    imageUrl,
    showImage,
    previewBody,
    contentLoaded,
    iframeLoaded,
    imageLoaded,
    setIframeLoaded,
    setImageLoaded,
    setImageFailed,
  } = useEmailPreviewSource({ initialPreview, manifest, siteId, variantId });

  // Stop/reset the hover scroll when the image is swapped (virtualized reuse) or
  // removed (showImage -> false, e.g. image error, unmounts the box without a
  // guaranteed mouseleave — leaving the interval running otherwise).
  useEffect(() => {
    handleImageMouseLeave();
  }, [imageUrl, showImage, handleImageMouseLeave]);

  return (
    <Box
      sx={{
        backgroundColor: "white",
        clipPath: CLIP_PATHS.top,
      }}
    >
      <Box sx={{ height: PREVIEW_HEIGHT, position: "relative" }}>
        <Skeleton
          variant="queen"
          width="100%"
          height="100%"
          sx={{
            position: "absolute",
            inset: 0,
            opacity: contentLoaded ? 0 : 1,
            transition: "opacity 0.3s ease-out",
          }}
        />

        {showImage && imageUrl && (
          <Box
            data-testid="email-card-preview-image"
            ref={imageScrollRef}
            onMouseEnter={handleImageMouseEnter}
            onMouseLeave={handleImageMouseLeave}
            sx={{
              position: "absolute",
              inset: 0,
              // hidden, not auto: the hover auto-scroll sets scrollTop
              // programmatically, but the user can't wheel/drag the preview.
              overflowY: "hidden",
              opacity: imageLoaded ? 1 : 0,
              transition: "opacity 0.3s ease-in",
            }}
          >
            <img
              ref={imageRef}
              src={imageUrl}
              alt={`${frameName} email preview`}
              loading="lazy"
              decoding="async"
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageFailed(true)}
              style={{
                display: "block",
                width: "100%",
                height: "auto",
              }}
            />
          </Box>
        )}

        {!showImage && previewBody !== undefined && (
          <Box
            onMouseEnter={handleIframeMouseEnter}
            onMouseLeave={handleIframeMouseLeave}
            sx={{
              position: "absolute",
              inset: 0,
              opacity: iframeLoaded ? 1 : 0,
              transition: "opacity 0.3s ease-in",
            }}
          >
            <iframe
              ref={iframeRef}
              title={`${frameName} email preview`}
              style={{
                display: "flex",
                flex: 1,
                position: "absolute",
                inset: 0,
                width: EMAIL_RENDER_WIDTH,
                aspectRatio: `${PREVIEW_WIDTH} / ${PREVIEW_HEIGHT}`,
                transform: `scale(${EMAIL_SCALE_FACTOR})`,
                transformOrigin: "top left",
                pointerEvents: "none",
              }}
              frameBorder={0}
              // Parent reads this frame's document; scripts stay blocked without allow-scripts.
              sandbox="allow-same-origin"
              srcDoc={wrapHtmlForScrollingFrame(previewBody)}
              tabIndex={-1}
              onLoad={() => {
                handleIframeLoad();
                setIframeLoaded(true);
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
